import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { HelpIcon } from '@src/components/icons/HelpIcon';
import { PlusIcon } from '@src/components/icons/PlusIcon';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Expandable.css';

export interface ExpandableProps {
  title: string;
  /** Disable the card surface when nested inside another panel. */
  elevated?: boolean;
  children?: ReactNode;
  /** Controlled. Omit it for an independent panel. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Leading mark. Defaults to the help disc. Pass `false` to hide it. */
  icon?: ReactNode | false;
  /** Parent id. Trigger, title, panel and body hang off it. */
  testId?: string;
  className?: string;
}

export interface ExpandableGroupProps {
  children: ReactNode;
  /** Which child starts open. `-1` leaves them all closed. */
  defaultOpenIndex?: number;
  /** Parent id. Each child without its own id becomes `${testId}-item-${index}`. */
  testId?: string;
  className?: string;
}

/**
 * One-at-a-time accordion. Pass Expandable children; the group owns which
 * one is open.
 */
export function ExpandableGroup({
  children,
  defaultOpenIndex = -1,
  testId,
  className,
}: ExpandableGroupProps) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);
  const classes = ['expandable-group'];
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')} {...testIdAttr(testId)}>
      {Children.map(children, (child, index) => {
        if (!isValidElement(child)) {
          return child;
        }
        const childProps = child.props as ExpandableProps;
        return cloneElement(child as ReactElement<ExpandableProps>, {
          open: openIndex === index,
          onOpenChange: (next: boolean) => setOpenIndex(next ? index : -1),
          testId: childProps.testId ?? testIdFor(testId, `item-${index}`),
        });
      })}
    </div>
  );
}

export function Expandable({
  title,
  elevated = true,
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  icon,
  testId,
  className,
}: ExpandableProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const triggerId = useId();
  const panelId = useId();

  function setOpen(next: boolean) {
    if (!controlled) {
      setUncontrolledOpen(next);
    }
    onOpenChange?.(next);
  }

  const mark = icon === false ? null : (icon ?? <HelpIcon />);

  const classes = ['expandable'];
  if (!elevated) classes.push('expandable--flat');
  if (open) {
    classes.push('expandable--open');
  }
  if (mark) {
    classes.push('expandable--has-mark');
  }
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')} {...testIdAttr(testId)}>
      <h3 className="expandable__heading">
        <button
          className="expandable__trigger"
          id={triggerId}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(!open)}
          {...testIdAttr(testIdFor(testId, 'trigger'))}
        >
          {mark ? (
            <span className="expandable__mark" aria-hidden="true">
              {mark}
            </span>
          ) : null}
          <Typography
            as="span"
            role="title/sub"
            className="expandable__title"
            {...testIdAttr(testIdFor(testId, 'title'))}
          >
            {title}
          </Typography>
          <span className="expandable__toggle" aria-hidden="true">
            <PlusIcon />
          </span>
        </button>
      </h3>
      <div
        className="expandable__panel"
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!open}
        inert={!open}
        {...testIdAttr(testIdFor(testId, 'panel'))}
      >
        <div className="expandable__clip">
          <Typography
            as="div"
            role="body/sm"
            className="expandable__body"
            {...testIdAttr(testIdFor(testId, 'body'))}
          >
            {children}
          </Typography>
        </div>
      </div>
    </div>
  );
}
